import Auction from '@/models/Auction';
import SavedAuction from '@/models/SavedAuction';
import AuctionClient from '@/api/AuctionClient';
import AuctionsClient from '@/api/AuctionsClient';
import featureFlagSlugs from '@/constants/featureFlagSlugs';
import cloneDeep from 'lodash.clonedeep';
import debounce from 'lodash.debounce';
import AuctionAdditionalDataHelpers from '@/utilities/marketplace/auctionAdditionalDataHelpers';
import {
  resolveSidebarCollectionKeys,
  getSidebarCollectionKeysToRemove,
  resolveStatusPillsFromEndedStatus,
} from '@/utilities/marketplace/sidebarCollectionResolver';

import {
  GET_MARKETPLACE_AUCTIONS_BY_CLIENT_METHOD,
  ASSIGN_COLLECTION_KEYS_TO_AUCTIONS,
  REMOVE_COLLECTION_KEYS_FROM_AUCTIONS,
  SET_AUCTION_VISIBILITY,
  ADD_AUCTIONS,
  REMOVE_AUCTIONS,
  UPDATE_AUCTIONS,
  SYNC_AUCTIONS_DATA,
  GET_AUCTIONS_DATA,
  SUBSCRIBE_MARKETPLACE_AUCTIONS,
  UNSUBSCRIBE_MARKETPLACE_AUCTIONS,
  UPDATE_SELECTED_AUCTION_LANE,
  UPDATE_SELECTED_AUCTION_LANE_SUB_FILTER,
  HANDLE_BID_MESSAGE,
  HANDLE_BID_API_RESPONSE,
  GET_AUCTION_FROM_VUEX_BY_AUCTION_ID,
  GET_AUCTIONS_FROM_VUEX_BY_AUCTION_IDS,
  HANDLE_END_MESSAGE,
  HANDLE_ENDED_UPDATE_MESSAGE,
  UPDATE_AD_HOC_SEARCH_IS_ACTIVE,
  UPDATE_SAVED_SEARCH_IS_ACTIVE,
  CLEAR_AUCTIONS,
  HANDLE_KILLED_MESSAGE,
  UPDATE_HAS_MORE_PAGES,
  UPDATE_AUCTION_LANES_REFRESHING,
  GET_LEGACY_CARFAX_ALERTS_ON_AUCTION,
  LOAD_VIEWING_AUCTION_MODEL,
  UPDATE_VIEWING_AUCTION_MODEL,
  CLEAR_VIEWING_AUCTION_MODEL,
  REFRESH_VIEWING_AUCTION_MODEL,
  UPDATE_LEGACY_AUCTION_RANK_IN_LIST,
  SUBSCRIBE_GRAPHQL_MARKETPLACE_AUCTIONS,
  RETAIN_GRAPHQL_SUBSCRIPTION,
  CLEAR_GRAPHQL_SUBSCRIPTIONS,
  HANDLE_GRAPHQL_UPDATE,
  FETCH_ADDITIONAL_DATA,
  RESOLVE_SIDEBAR_COLLECTION_KEYS,
  UPDATE_AUCTION_BID_INCREMENT,
} from './marketplace-auctions.actions.type';
import { GET_AUCTION_IDS, GET_SUBSCRIPTION_BY_AUCTION_ID } from './marketplace-auctions.getters.type';
import {
  UPDATE_AUCTIONS as UPDATE_AUCTION_MUTATION,
  UPDATE_SELECTED_AUCTION_LANE as UPDATE_SELECTED_AUCTION_LANE_MUTATION,
  UPDATE_SELECTED_AUCTION_LANE_SUB_FILTER as UPDATE_SELECTED_AUCTION_LANE_SUB_FILTER_MUTATION,
  SET_SYNC_AUCTIONS_DATA_REQUEST as SET_SYNC_AUCTIONS_DATA_REQUEST_MUTATION,
  ADD_AUCTION_IDS_TO_WAITING_FOR_SYNC_COLLECTION,
  CLEAR_AUCTION_IDS_WAITING_FOR_SYNC_COLLECTION,
  UPDATE_AD_HOC_SEARCH_IS_ACTIVE as UPDATE_AD_HOC_SEARCH_IS_ACTIVE_MUTATION,
  UPDATE_SAVED_SEARCH_IS_ACTIVE as UPDATE_SAVED_SEARCH_IS_ACTIVE_MUTATION,
  UPDATE_LAST_SYNC_AUCTIONS_DATA_REQUEST_TIMESTAMP,
  CLEAR_AUCTIONS as CLEAR_AUCTIONS_MUTATION,
  REMOVE_AUCTIONS as REMOVE_AUCTIONS_MUTATION,
  UPDATE_HAS_MORE_PAGES as UPDATE_HAS_MORE_PAGES_MUTATION,
  UPDATE_VIEWING_AUCTION_MODEL as UPDATE_VIEWING_AUCTION_MODEL_MUTATION,
  UPDATE_LEGACY_AUCTION_RANK_IN_LIST as UPDATE_LEGACY_AUCTION_RANK_IN_LIST_MUTATION,
  UPDATE_AUCTION_LANES_REFRESHING as UPDATE_AUCTION_LANES_REFRESHING_MUTATION,
  RETAIN_GRAPHQL_SUBSCRIPTION as RETAIN_GRAPHQL_SUBSCRIPTION_MUTATION,
  REMOVE_GRAPHQL_SUBSCRIPTION_BY_AUCTION_IDS,
  UPDATE_AUCTION_BID_INCREMENT as UPDATE_AUCTION_BID_INCREMENT_MUTATION,
  UPDATE_VIEWING_AUCTION_BID_INCREMENT as UPDATE_VIEWING_AUCTION_BID_INCREMENT_MUTATION,
} from './marketplace-auctions.mutations.type';

const syncAuctionsDataRequestDebounceInterval = 5000;

function getStartingIndex(auctions, collectionKey) {
  const indexes = auctions.flatMap((auction) => auction?.includeInCollections || [])
    .filter((c) => c.fetchMethod === collectionKey)
    .map((c) => c.index);

  return indexes.length ? Math.max(...indexes) : 0;
}

function adjustAuctionStatusPills(
  highBid,
  highBidderUserId,
  highBidderDealerId,
  currentUser,
  { floorPrice, participant, autosell },
  { statusPills },
) {
  const arr = [];

  if (autosell && statusPills?.includes('no_reserve')) {
    // show no reserve if auction is no reserve always
    arr.push('no_reserve');
  } else if (autosell && highBid >= floorPrice) {
    // else show reserve met when reached
    arr.push('reserve_met');
  } else if (statusPills?.includes('near_reserve') || statusPills?.includes('one_bid_away')) {
    // else carry over the reserve badges from the server; the display flag picks which one is shown
    ['near_reserve', 'one_bid_away'].forEach((pill) => {
      if (statusPills.includes(pill)) arr.push(pill);
    });
  } else if (statusPills?.includes('low_reserve')) {
    // else show low reserve
    arr.push('low_reserve');
  }

  if (currentUser.isMemberOfWinningDealership(highBidderDealerId)
    || highBidderUserId === currentUser.id) {
    arr.push('high_bidder');
  }

  if (highBidderUserId !== currentUser.id && participant) {
    arr.push('out_bid');
  }

  // show those statuses
  return arr;
}

function isValidViewingAuctionModel(viewingAuctionModel) {
  // a valid viewing auction model has properties and is not a saved auction for syncing purposes
  return Object.keys(viewingAuctionModel).length && !(viewingAuctionModel instanceof SavedAuction);
}

async function isOnSrpRefreshWithGraphQl(rootState, dispatch) {
  const usingGraphQlOnSrp = await dispatch('getFlag', featureFlagSlugs.enableGraphqlOnSrp, { root: true });
  if (!usingGraphQlOnSrp) {
    return false;
  }

  return rootState?.route?.name === 'Marketplace';
}

const actions = {
  async [GET_MARKETPLACE_AUCTIONS_BY_CLIENT_METHOD]({
    rootGetters,
    dispatch,
  }, {
    fetchMethod,
    clearCollectionKeys = false,
    params = [],
    paramsObj = {},
  }) {
    const auctionsClient = new AuctionsClient(rootGetters.currentUser);
    if (typeof auctionsClient[fetchMethod] !== 'function') {
      if (import.meta.env.DEV) {
        window.console.error(`Fetch Method "${fetchMethod}" is not defined on AuctionsClient. Aborting.`);
      }
      return false;
    }

    let response;
    if (Object.keys(paramsObj).length) {
      response = await auctionsClient[fetchMethod](paramsObj);
    } else {
      response = await auctionsClient[fetchMethod](...params);
    }

    const {
      results: auctions,
    } = response;
    let auctionsWithCollectionKeys = [...auctions];
    if (auctions.length > 0) {
      const auctionIds = auctions.map((a) => a.id);
      const existingAuctions = await dispatch(
        GET_AUCTIONS_FROM_VUEX_BY_AUCTION_IDS,
        { auctionIds },
      );

      const updatedAuctions = auctions.map((auction) => {
        const existingAuction = existingAuctions.find((ea) => ea.id === auction.id);
        if (existingAuction && !clearCollectionKeys) {
          return {
            ...existingAuction,
            ...auction,
          };
        }

        return auction;
      });

      // Assign the received auctions to their specific collection key
      auctionsWithCollectionKeys = await dispatch(
        ASSIGN_COLLECTION_KEYS_TO_AUCTIONS,
        {
          auctions: updatedAuctions,
          collectionKeys: [fetchMethod],
        },
      );

      // Subscribe to those received auctions
      await dispatch(SUBSCRIBE_MARKETPLACE_AUCTIONS, {
        auctions: auctionsWithCollectionKeys,
      });

      // Re-resolve sidebar collection keys for each fetched auction so backend-lag
      // responses don't re-add auctions the real-time path already transitioned out.
      await Promise.all(
        auctionsWithCollectionKeys.map((auction) => dispatch(
          RESOLVE_SIDEBAR_COLLECTION_KEYS,
          { auction },
        )),
      );
    }

    // Fetch additional data without blocking
    dispatch(FETCH_ADDITIONAL_DATA, { auctions: auctionsWithCollectionKeys });

    return {
      ...response,
      results: [
        ...auctionsWithCollectionKeys,
      ],
    };
  },
  [ASSIGN_COLLECTION_KEYS_TO_AUCTIONS](
    {
      commit,
      state,
    },
    { auctions, collectionKeys = [] },
  ) {
    const selectedLaneFetchMethod = state.selectedAuctionLane?.fetchMethod;
    const selectedLaneAdjustIndexes = state.selectedAuctionLane?.adjustIndexes;

    // Placement for each auction in each collection
    // existing members keep their index
    // new members are appended after the current max
    const auctionCollectionsArray = collectionKeys.flatMap((c) => {
      // grab the start index for the auctions of the collection
      let startIndexForCollection = state.auctions.length
        ? getStartingIndex(state.auctions, c) + 1
        : 0;

      return auctions.map((auction) => {
        const auctionInState = state.auctions.find((a) => a.id === auction.id);
        const existingCollection = auctionInState?.includeInCollections
          ?.find((ic) => ic.fetchMethod === c);

        let index = existingCollection?.index;

        // no collection, increment the start index for the new auction in this new collection
        if (!existingCollection) {
          index = startIndexForCollection;
          startIndexForCollection += 1;
        }

        return {
          auctionId: auction.id,
          index,
          fetchMethod: c,
          endTime: auction.endTime,
        };
      });
    });

    const updatedAuctions = auctions.map((auction) => {
      /*
      * If the indexes should be shifted due to insertions by the end time of new auctions
      * within the listing, then grab the auction where this new auction would be placed beside
      * and set the new auction collection index to be the same index of the auction it is being
      * placed beside
      */
      if (selectedLaneAdjustIndexes
        && selectedLaneFetchMethod
        && state.auctions.length
      ) {
        const relevantCollection = state.auctions.reduce((arr, a) => {
          if ((a?.includeInCollections || []).length) {
            const foundCollection = a.includeInCollections
              .find((c) => c.fetchMethod === selectedLaneFetchMethod);
            if (foundCollection) {
              arr.push(foundCollection);
            }
          }
          return arr;
        }, []);

        // find the first occurrence of a collection to compare from the sorted collections
        const collectionToCompare = relevantCollection
          .sort((a, b) => a.endTime - b.endTime)
          .find((c) => auction.endTime < c.endTime);

        if (collectionToCompare) {
          auctionCollectionsArray.forEach((c) => {
            const collectionToAdjust = c;
            if (collectionToAdjust.auctionId === auction.id
              && c?.fetchMethod === selectedLaneFetchMethod) {
              // the adjusted auction should appear above the found auction index
              // since the adjusted auction ends before the found auction
              collectionToAdjust.index = collectionToCompare.index - 1;
            }
          });
        }
      }

      // Include the collections for each auction to be sent off to the mutations
      const collectionsForAuction = auctionCollectionsArray
        .filter((a) => a.auctionId === auction.id)
        .map((a) => ({
          fetchMethod: a.fetchMethod,
          index: a.index,
          endTime: a.endTime,
        }));

      const auctionInState = state.auctions.find((a) => a.id === auction.id);
      // Always prefer vuex state auction includeInCollections then the api auction object
      // as the collections are managed in vuex
      const existingCollections = [
        ...(auctionInState?.includeInCollections || auction?.includeInCollections || []),
      ];

      // If there was existing collections for the auction, include collections except the
      // ones already mapped
      if (existingCollections.length) {
        const mappedFetchMethods = collectionsForAuction.map((c) => c.fetchMethod);
        existingCollections.forEach((e) => {
          if (!mappedFetchMethods.includes(e.fetchMethod)) {
            collectionsForAuction.push(e);
          }
        });
      }

      return {
        ...auction,
        includeInCollections: [
          ...collectionsForAuction,
        ],
      };
    });
    commit(UPDATE_AUCTION_MUTATION, updatedAuctions);

    return updatedAuctions;
  },
  [REMOVE_COLLECTION_KEYS_FROM_AUCTIONS](
    {
      commit,
    },
    { auctions, collectionKeys = [] },
  ) {
    const updatedAuctions = auctions.map((auction) => ({
      ...auction,
      includeInCollections: (auction.includeInCollections || [])
        .filter((a) => !collectionKeys.includes(a.fetchMethod)),
    }));
    const auctionsToRemove = updatedAuctions.filter((a) => a.includeInCollections.length === 0);
    const auctionsToUpdate = updatedAuctions.filter((a) => a.includeInCollections.length > 0);
    commit(REMOVE_AUCTIONS_MUTATION, auctionsToRemove);
    commit(UPDATE_AUCTION_MUTATION, auctionsToUpdate);

    return {
      removedAuctions: auctionsToRemove,
      updatedAuctions: auctionsToUpdate,
    };
  },
  [SET_AUCTION_VISIBILITY](
    {
      commit,
      state,
      getters,
    },
    { auctionId, visible },
  ) {
    if (getters[GET_AUCTION_IDS].includes(auctionId)) {
      const existingAuction = state.auctions.find((a) => a.id === auctionId);
      if (existingAuction) {
        const updatedAuction = {
          ...existingAuction,
          visible,
        };
        commit(UPDATE_AUCTION_MUTATION, [updatedAuction]);
        return updatedAuction;
      }
    }
    return null;
  },
  [ADD_AUCTIONS](
    {
      commit,
      dispatch,
    },
    { auctions },
  ) {
    commit(ADD_AUCTIONS, auctions);
    dispatch(SUBSCRIBE_MARKETPLACE_AUCTIONS, { auctions });
  },
  async [REMOVE_AUCTIONS](
    {
      commit,
      dispatch,
    },
    { auctions },
  ) {
    await dispatch(UNSUBSCRIBE_MARKETPLACE_AUCTIONS, { auctions });
    commit(REMOVE_AUCTIONS, auctions);
  },
  [UPDATE_AUCTIONS](
    {
      commit,
    },
    { auctions },
  ) {
    commit(UPDATE_AUCTION_MUTATION, auctions);
  },
  async [CLEAR_AUCTIONS](
    {
      commit,
      dispatch,
      state,
    },
  ) {
    await dispatch(UNSUBSCRIBE_MARKETPLACE_AUCTIONS, { auctions: state.auctions, all: true });
    commit(CLEAR_AUCTIONS_MUTATION);
    commit(CLEAR_AUCTION_IDS_WAITING_FOR_SYNC_COLLECTION);
  },
  [UPDATE_HAS_MORE_PAGES]({
    commit,
  }, {
    hasMorePages,
  }) {
    commit(UPDATE_HAS_MORE_PAGES_MUTATION, hasMorePages);
  },
  [UPDATE_AUCTION_LANES_REFRESHING]({
    commit,
  }, {
    refreshing,
  }) {
    commit(UPDATE_AUCTION_LANES_REFRESHING_MUTATION, refreshing);
  },
  async [GET_LEGACY_CARFAX_ALERTS_ON_AUCTION](
    /*
    * Get the legacy carfax alerts from the vin and auction id
    */
    {
      dispatch,
      rootGetters,
    },
    { vin, id },
  ) {
    let carfaxAlerts;
    try {
      const dealerId = rootGetters.currentUser.defaultDealerId;
      carfaxAlerts = await dispatch('auctions/getCarfaxReport', {
        vin,
        auctionId: id,
        dealerId,
      }, { root: true });
    } catch (carfaxException) {
      // TODO - Do we want to error or not? - ETC
      // await dispatch('auctions/setError', {
      //   message: 'There was a problem retrieving the Carfax Report. Please try again later.',
      // });
    }
    return Promise.resolve(carfaxAlerts?.alerts);
  },
  [SYNC_AUCTIONS_DATA](
    {
      commit,
      dispatch,
      state,
      rootGetters: {
        isAuthorized,
      },
    },
    { auctionIds, retries = 3, force = false },
  ) {
    if (isAuthorized) {
      const timeDifference = ((Date.now()) - state.lastSyncAuctionsDataRequestTimestamp.getTime());
      const intervalElapsed = timeDifference >= syncAuctionsDataRequestDebounceInterval;
      if (intervalElapsed || force) {
        if (state.syncAuctionsDataRequest === null) {
          const { auctionIdsWaitingToSync } = state;
          const auctionIdsToUpdate = [...new Set([
            ...auctionIdsWaitingToSync,
            ...auctionIds,
          ])];

          // whenever a sync operation happens from any updates,
          // update the viewing auction model if its there
          if (isValidViewingAuctionModel(state?.viewingAuctionModel)) {
            if (!auctionIdsToUpdate.includes(state.viewingAuctionModel.id)) {
              auctionIdsToUpdate.push(state.viewingAuctionModel.id);
            }
          }

          const existingAuctionIdsWaitingToSync = state.auctions
            .filter((a) => auctionIdsToUpdate.includes(a.id))
            .map((b) => b.id);
          if (existingAuctionIdsWaitingToSync.length > 0) {
            commit(UPDATE_LAST_SYNC_AUCTIONS_DATA_REQUEST_TIMESTAMP, new Date());
            const syncAuctionsDataRequest = dispatch(
              GET_AUCTIONS_DATA,
              {
                auctionIds: existingAuctionIdsWaitingToSync,
                retries,
              },
            ).then(({ results: auctions }) => {
              commit(CLEAR_AUCTION_IDS_WAITING_FOR_SYNC_COLLECTION);
              commit(UPDATE_AUCTION_MUTATION, auctions);

              // Fetch additional data for synced auctions without blocking
              // Currently, the additional data is not required for polling
              // const syncedAuctions = state.auctions
              //   .filter((a) => existingAuctionIdsWaitingToSync.includes(a.id));
              // dispatch(FETCH_ADDITIONAL_DATA, { auctions: syncedAuctions });

              if (isValidViewingAuctionModel(state?.viewingAuctionModel)) {
                dispatch(REFRESH_VIEWING_AUCTION_MODEL);
              }
            }).catch(() => {
              // on err catch, retain those ids to be re-synced
              commit(ADD_AUCTION_IDS_TO_WAITING_FOR_SYNC_COLLECTION, auctionIds);
            }).finally(() => {
              // when request finishes successfully or in error, allow subsequent requests to happen
              commit(SET_SYNC_AUCTIONS_DATA_REQUEST_MUTATION, null);
            });

            // acknowledge that a request is happening
            commit(SET_SYNC_AUCTIONS_DATA_REQUEST_MUTATION, syncAuctionsDataRequest);
          }
        } else {
          commit(ADD_AUCTION_IDS_TO_WAITING_FOR_SYNC_COLLECTION, auctionIds);
        }
      } else {
        commit(ADD_AUCTION_IDS_TO_WAITING_FOR_SYNC_COLLECTION, auctionIds);
      }
    }
  },
  async [GET_AUCTIONS_DATA](
    {
      rootGetters,
    },
    { auctionIds, retries = 3 },
  ) {
    const auctionsClient = new AuctionsClient(rootGetters.currentUser);
    const response = await auctionsClient.getAuctionsById(auctionIds, retries);
    return response;
  },
  async [SUBSCRIBE_MARKETPLACE_AUCTIONS]({
    rootState,
    dispatch,
  }, { auctions }) {
    if (!(await isOnSrpRefreshWithGraphQl(rootState, dispatch))) {
      const activeAuctionChannelKeys = auctions.map((auction) => `updates.${auction.id}`);
      const endedAuctionChannelKeys = auctions
        .map((auction) => `ended_updates.${auction.id}`);
      await dispatch('subscribeToChannels', [...activeAuctionChannelKeys, ...endedAuctionChannelKeys], { root: true });
    }
  },
  async [UNSUBSCRIBE_MARKETPLACE_AUCTIONS]({
    rootState,
    dispatch,
  }, { auctions = [], all = false }) {
    if (await isOnSrpRefreshWithGraphQl(rootState, dispatch)) {
      dispatch(CLEAR_GRAPHQL_SUBSCRIPTIONS, {
        auctionIds: auctions.map((a) => a.id),
        all,
      });
    } else {
      const activeAuctionChannelKeys = auctions.map((auction) => `updates.${auction.id}`);
      const endedAuctionChannelKeys = auctions
        .map((auction) => `ended_updates.${auction.id}`);
      await dispatch('unsubscribeToChannels', [...activeAuctionChannelKeys, ...endedAuctionChannelKeys], { root: true });
    }
  },
  [UPDATE_SELECTED_AUCTION_LANE](
    {
      commit,
    },
    selectedAuctionLane,
  ) {
    commit(UPDATE_SELECTED_AUCTION_LANE_MUTATION, selectedAuctionLane);
  },
  [UPDATE_SELECTED_AUCTION_LANE_SUB_FILTER](
    {
      commit,
    },
    selectedAuctionLaneSubFilter,
  ) {
    commit(UPDATE_SELECTED_AUCTION_LANE_SUB_FILTER_MUTATION, selectedAuctionLaneSubFilter);
  },
  [LOAD_VIEWING_AUCTION_MODEL](
    /*
    * Load both the easypass auction and the legacy auction requests
    * Then update auction the easy pass version and the legacy version
    */
    {
      dispatch,
      rootGetters,
    },
    { auctionId },
  ) {
    const auctionClient = new AuctionClient(rootGetters.currentUser);
    const promises = [
      dispatch(GET_AUCTIONS_DATA, { auctionIds: [auctionId] }), // easypass
      auctionClient.get(auctionId), // legacy
    ];
    return new Promise((resolve, reject) => {
      Promise.all(promises).then(([
        { results: [easyPassViewingAuction] },
        { data: legacyViewingAuction },
      ]) => {
        // update easypass auctions with the easypass response
        dispatch(UPDATE_AUCTIONS, { auctions: [easyPassViewingAuction] });
        // grab the carfax alerts and update the viewing auction with the legacy response
        dispatch(GET_LEGACY_CARFAX_ALERTS_ON_AUCTION, {
          vin: legacyViewingAuction.vin,
          id: legacyViewingAuction.id,
        }).then((carfaxAlerts) => {
          const modifiedLegacyViewingAuction = legacyViewingAuction;

          modifiedLegacyViewingAuction.carfax_alerts = carfaxAlerts;
          dispatch(UPDATE_VIEWING_AUCTION_MODEL, { auction: modifiedLegacyViewingAuction })
            .then(() => resolve());
        });
      }).catch((e) => {
        reject(e);
      });
    });
  },
  [UPDATE_VIEWING_AUCTION_MODEL](
    /*
    * Takes an auction object to create an Auction model class
    * Updates the viewing auction model
    */
    {
      commit,
      dispatch,
      state,
      rootGetters,
    },
    { auction, saved = false },
  ) {
    let auctionModel;
    if (saved || state?.viewingAuctionModel instanceof SavedAuction) {
      auctionModel = new SavedAuction(auction, rootGetters.currentUser);
    } else {
      auctionModel = new Auction(auction, rootGetters.currentUser);
      auctionModel.copyAuctionFromAdditionalData(state?.viewingAuctionModel);
    }

    // if the viewing auction exists, either regular or saved and
    // the auction received does not have any carfax alerts,
    // set the carfax alerts that were initially loaded
    if (Object.keys(state?.viewingAuctionModel).length) {
      if (auctionModel.id === state.viewingAuctionModel.id
        && (!auctionModel.carfaxAlerts || !Object.keys(auctionModel.carfaxAlerts).length)
      ) {
        auctionModel.carfaxAlerts = state.viewingAuctionModel.carfaxAlerts;
      }

      // carry forward a previously polled bid increment so a full-model
      // refresh whose payload lacks bid_increment does not drop it
      if (auctionModel.id === state.viewingAuctionModel.id
        && auctionModel.bidIncrement == null
        && state.viewingAuctionModel.bidIncrement != null
      ) {
        auctionModel.bidIncrement = state.viewingAuctionModel.bidIncrement;
      }
    }
    commit(UPDATE_VIEWING_AUCTION_MODEL_MUTATION, auctionModel);
    dispatch(FETCH_ADDITIONAL_DATA, { auctions: [auctionModel], forSingleAuction: true });
  },
  [UPDATE_AUCTION_BID_INCREMENT](
    /*
    * Applies a polled dynamic bid increment to
    * the SRP auctions array entry and, when it is the auction
    * currently being viewed, the viewing auction model
    */
    { commit, state },
    { id, bidIncrement },
  ) {
    commit(UPDATE_AUCTION_BID_INCREMENT_MUTATION, { id, bidIncrement });
    if (state.viewingAuctionModel?.id === id) {
      commit(UPDATE_VIEWING_AUCTION_BID_INCREMENT_MUTATION, bidIncrement);
    }
  },
  async [CLEAR_VIEWING_AUCTION_MODEL](
    /*
    * Clear the viewing auction and dispatch to unsubscribe channels
    */
    {
      dispatch,
      commit,
      state,
    },
  ) {
    if (!state.auctions.find((a) => a.id === state.viewingAuctionModel?.auction?.id)) {
      await dispatch('auctions/unsubscribeAuctions', 'marketplaceAuctions', { root: true });
    }

    commit(UPDATE_VIEWING_AUCTION_MODEL_MUTATION, {});
    await dispatch(UPDATE_LEGACY_AUCTION_RANK_IN_LIST, { rankInList: null });
  },
  async [REFRESH_VIEWING_AUCTION_MODEL](
    /*
    * Refreshes the existing viewing auction and updates in state
    */
    {
      dispatch,
      rootGetters,
      state,
    },
  ) {
    const auctionClient = new AuctionClient(rootGetters.currentUser);
    const response = await auctionClient.get(state.viewingAuctionModel.id);
    const auctionResponseToUpdate = response.data;

    await dispatch(UPDATE_VIEWING_AUCTION_MODEL, { auction: auctionResponseToUpdate });
  },
  [UPDATE_LEGACY_AUCTION_RANK_IN_LIST](
    {
      commit,
    },
    { rankInList },
  ) {
    commit(UPDATE_LEGACY_AUCTION_RANK_IN_LIST_MUTATION, rankInList);
  },
  [UPDATE_AD_HOC_SEARCH_IS_ACTIVE](
    {
      commit,
    },
    { adHocSearchIsActive },
  ) {
    commit(UPDATE_AD_HOC_SEARCH_IS_ACTIVE_MUTATION, adHocSearchIsActive);
  },
  [UPDATE_SAVED_SEARCH_IS_ACTIVE](
    {
      commit,
    },
    { savedSearchIsActive },
  ) {
    commit(UPDATE_SAVED_SEARCH_IS_ACTIVE_MUTATION, savedSearchIsActive);
  },
  [GET_AUCTION_FROM_VUEX_BY_AUCTION_ID](
    {
      state,
    },
    { auctionId },
  ) {
    return state.auctions.find((auction) => auction.id === auctionId);
  },
  [GET_AUCTIONS_FROM_VUEX_BY_AUCTION_IDS](
    {
      state,
    },
    { auctionIds },
  ) {
    return state.auctions.filter((auction) => auctionIds.includes(auction.id));
  },
  async [HANDLE_BID_MESSAGE](
    {
      dispatch,
      state,
      rootGetters,
    },
    pubNubMessage,
  ) {
    const {
      /*
       * Leaving unused fields commented out in the event we need them in the future - ETC
       */
      auction: {
        // bid_count,
        current_bid: highBid,
        end_time: endTime,
        high_bidder_dealer_id: highBidderDealerId,
        high_bidder_id: highBidderUserId,
        id: auctionId,
        // seller_dealer_id,
        // seller_id,
        state: status,
        // unique_bidders,
      },
    } = pubNubMessage;
    const existingAuction = state.auctions.find((auction) => auction.id === auctionId);
    if (existingAuction) {
      const { currentUser } = rootGetters;
      const updatedValues = {
        buyerInfo: {
          dealerId: highBidderDealerId,
          userId: highBidderUserId,
          highBid,
        },
        participant: existingAuction.participant || highBidderUserId === currentUser.id,
        canBid: highBidderUserId !== currentUser.id && status === 'active',
        statusPills: [],
        endTime,
        highBid,
        isMemberOfTopBiddingDealership: currentUser.isMemberOfWinningDealership(highBidderDealerId),
        isPrimaryTopBidder: highBidderUserId === currentUser.id,
      };
      const updatedAuction = {
        ...existingAuction,
        ...updatedValues,
      };

      updatedAuction.statusPills = adjustAuctionStatusPills(
        highBid,
        highBidderUserId,
        highBidderDealerId,
        currentUser,
        updatedAuction,
        existingAuction,
      );

      await dispatch(UPDATE_AUCTIONS, { auctions: [updatedAuction] });
      await dispatch(RESOLVE_SIDEBAR_COLLECTION_KEYS, { auction: updatedAuction });
      await dispatch(SYNC_AUCTIONS_DATA, { auctionIds: [updatedAuction.id] });
    }
  },
  async [HANDLE_END_MESSAGE](
    {
      dispatch,
      state,
      rootGetters,
    },
    pubNubMessage,
  ) {
    const {
      auction: {
        current_bid: highBid,
        high_bidder_dealer_id: highBidderDealerId,
        high_bidder_id: highBidderUserId,
        id: auctionId,
        end_time: endTime,
        state: status,
      },
    } = pubNubMessage;
    const existingAuction = state.auctions.find((auction) => auction.id === auctionId);
    if (existingAuction) {
      const { currentUser } = rootGetters;
      const updatedValues = {
        buyerInfo: {
          dealerId: highBidderDealerId,
          userId: highBidderUserId,
          highBid,
        },
        endTime,
        status,
        participant: existingAuction.participant || highBidderUserId === currentUser.id,
        isMemberOfTopBiddingDealership: currentUser.isMemberOfWinningDealership(highBidderDealerId),
        isPrimaryTopBidder: highBidderUserId === currentUser.id,
      };
      const updatedAuction = {
        ...existingAuction,
        ...updatedValues,
        statusPills: resolveStatusPillsFromEndedStatus(
          status,
          updatedValues.participant,
        ), // optimistic changes
      };
      await dispatch(UPDATE_AUCTIONS, { auctions: [updatedAuction] });
      await dispatch(RESOLVE_SIDEBAR_COLLECTION_KEYS, { auction: updatedAuction });
      await dispatch(SYNC_AUCTIONS_DATA, { auctionIds: [updatedAuction.id] });
    } else if (isValidViewingAuctionModel(state?.viewingAuctionModel)) {
      await dispatch(REFRESH_VIEWING_AUCTION_MODEL);
    }
  },
  async [HANDLE_ENDED_UPDATE_MESSAGE](
    {
      dispatch,
      state,
    },
    pubNubMessage,
  ) {
    const {
      auction: {
        id: unparsedAuctionId,
        bid_amount: bidAmount,
      },
    } = pubNubMessage;

    if (typeof bidAmount === 'undefined' || bidAmount === null) {
      const auctionId = Number.parseInt(unparsedAuctionId, 10);
      const existingAuction = state.auctions.find((auction) => auction.id === auctionId);
      if (existingAuction) {
        await dispatch(SYNC_AUCTIONS_DATA, { auctionIds: [auctionId] });
      } else if (isValidViewingAuctionModel(state?.viewingAuctionModel)) {
        await dispatch(REFRESH_VIEWING_AUCTION_MODEL);
      }
    }
  },
  async [HANDLE_KILLED_MESSAGE](
    {
      dispatch,
      state,
    },
    pubNubMessage,
  ) {
    const {
      auction: {
        // current_bid: currentBid,
        end_time: endTime,
        // high_bidder_dealer_id: highBidderDealerId,
        // high_bidder_id: highBidUserId,
        id: auctionId,
        // seller_dealer_id: sellerDealerId,
        // seller_id: sellerUserId,
        state: status,
        // unique_bidders: uniqueBidders,
      },
    } = pubNubMessage;
    const existingAuction = state.auctions.find((auction) => auction.id === auctionId);
    if (existingAuction) {
      const updatedAuction = {
        ...existingAuction,
        ...{
          status,
          endTime,
        },
      };
      await dispatch(UPDATE_AUCTIONS, { auctions: [updatedAuction] });
    }

    if (isValidViewingAuctionModel(state?.viewingAuctionModel)) {
      await dispatch(REFRESH_VIEWING_AUCTION_MODEL);
    }
  },
  async [HANDLE_BID_API_RESPONSE](
    {
      dispatch,
      state,
      rootGetters,
    },
    auctionBidResponse,
  ) {
    const {
      auctionId,
      auction: {
        highBidInfo: {
          amount: highBid,
          userId: highBidderUserId,
          dealerId: highBidderDealerId,
        },
        auctionStatus: status,
        endTime,
        participant,
      },
    } = auctionBidResponse;
    const existingAuction = state.auctions.find((auction) => auction.id === auctionId);
    if (existingAuction) {
      const { currentUser } = rootGetters;
      const updatedValues = {
        buyerInfo: {
          dealerId: highBidderDealerId,
          userId: highBidderUserId,
          highBid,
        },
        endTime,
        participant,
        statusPills: [],
        status,
        canBid: highBidderUserId !== currentUser.id && status === 'active',
        highBid,
        isMemberOfTopBiddingDealership: currentUser.isMemberOfWinningDealership(highBidderDealerId),
        isPrimaryTopBidder: highBidderUserId === currentUser.id,
      };

      const updatedAuction = {
        ...existingAuction,
        ...updatedValues,
      };

      updatedAuction.statusPills = adjustAuctionStatusPills(
        highBid,
        highBidderUserId,
        highBidderDealerId,
        currentUser,
        updatedAuction,
        existingAuction,
      );

      await dispatch(UPDATE_AUCTIONS, { auctions: [updatedAuction] });
      await dispatch(RESOLVE_SIDEBAR_COLLECTION_KEYS, { auction: updatedAuction });
      await dispatch(SYNC_AUCTIONS_DATA, { auctionIds: [updatedAuction.id] });
    }
  },

  [SUBSCRIBE_GRAPHQL_MARKETPLACE_AUCTIONS]({
    dispatch,
    rootGetters,
  }, { auctionIds = [] }) {
    const auctionsClient = new AuctionsClient(rootGetters.currentUser);

    auctionIds.forEach((id) => {
      // create a subscription per auction if not already being tracked
      auctionsClient.subscribeAuctionsByIds({
        auctionIds: [id],
        callbackFn: (data) => {
          if (Object.keys(data.auctionLiveUpdates).length) {
            dispatch(HANDLE_GRAPHQL_UPDATE, { auction: data.auctionLiveUpdates });
          }
        },
        handleSubscriptionFn: (trackedSubscription) => {
          dispatch(RETAIN_GRAPHQL_SUBSCRIPTION, {
            subscription: {
              ...trackedSubscription,
            },
          });
        },
      });
    });
  },

  [RETAIN_GRAPHQL_SUBSCRIPTION](
    {
      getters,
      commit,
    },
    { subscription = {} },
  ) {
    const auctionId = subscription.variables.auction_ids?.[0];

    if (!(getters[GET_SUBSCRIPTION_BY_AUCTION_ID](auctionId))) {
      commit(RETAIN_GRAPHQL_SUBSCRIPTION_MUTATION, subscription);
    }
  },

  [CLEAR_GRAPHQL_SUBSCRIPTIONS](
    {
      state,
      commit,
      rootGetters,
    },
    { auctionIds = [], all = false },
  ) {
    const auctionsClient = new AuctionsClient(rootGetters.currentUser);
    let subscriptionIdsToRemove = [];
    let auctionIdsToRemove = [];

    if (all) {
      // remove all subscriptions from main listing and fast action panel
      subscriptionIdsToRemove = Object.values(state.auctionSubscriptions ?? {})
        .map((s) => s.id);
      auctionIdsToRemove = auctionIds;
    } else if (auctionIds.length) {
      auctionIds.forEach((id) => {
        // remove subscriptions from a provided fetch method
        const originatingSubscription = state.auctionSubscriptions?.[id];

        if (originatingSubscription
          && !subscriptionIdsToRemove.includes(originatingSubscription.id)
        ) {
          subscriptionIdsToRemove.push(originatingSubscription.id);
          auctionIdsToRemove.push(id);
        }
      });
    }

    subscriptionIdsToRemove.forEach((id) => {
      auctionsClient.unsubscribeAuctionSubscription({ subscriptionId: id });
    });

    commit(REMOVE_GRAPHQL_SUBSCRIPTION_BY_AUCTION_IDS, auctionIdsToRemove);
  },

  async [HANDLE_GRAPHQL_UPDATE](
    {
      dispatch,
      state,
    },
    { auction = {} },
  ) {
    const existingAuction = state.auctions.find((a) => a.id === auction.id);

    if (existingAuction) {
      const updatedAuction = cloneDeep({
        ...existingAuction,
        ...auction,
      });

      await dispatch(UPDATE_AUCTIONS, { auctions: [updatedAuction] });
      await dispatch(RESOLVE_SIDEBAR_COLLECTION_KEYS, { auction: updatedAuction });

      if (isValidViewingAuctionModel(state?.viewingAuctionModel)
        && state?.viewingAuctionModel.id === auction.id
      ) {
        await dispatch(REFRESH_VIEWING_AUCTION_MODEL);
      }
    }
  },

  async [RESOLVE_SIDEBAR_COLLECTION_KEYS](
    { dispatch },
    { auction },
  ) {
    const [auctionInState] = await dispatch(
      GET_AUCTIONS_FROM_VUEX_BY_AUCTION_IDS,
      { auctionIds: [auction.id] },
    );
    // includeInCollections is Vuex-managed; merge it in to correctly remove stale sidebar keys
    const auctionWithCollections = auctionInState
      ? { ...auctionInState, ...auction }
      : auction;

    const resolved = resolveSidebarCollectionKeys(auctionWithCollections);
    const toRemove = getSidebarCollectionKeysToRemove(auctionWithCollections, resolved);

    let auctionToAssign = auctionWithCollections;
    if (toRemove.length) {
      const { updatedAuctions, removedAuctions } = await dispatch(
        REMOVE_COLLECTION_KEYS_FROM_AUCTIONS,
        { auctions: [auctionWithCollections], collectionKeys: toRemove },
      );
      auctionToAssign = updatedAuctions[0] ?? removedAuctions[0] ?? auctionWithCollections;
    }
    if (resolved.length) {
      await dispatch(ASSIGN_COLLECTION_KEYS_TO_AUCTIONS, {
        auctions: [auctionToAssign],
        collectionKeys: resolved,
      });
    }
  },

  async [FETCH_ADDITIONAL_DATA](
    {
      state, commit, dispatch, rootState,
    },
    {
      auctions,
      /** @type {boolean} for VDP page which uses VIEWING_AUCTION_MODEL */
      forSingleAuction,
    },
  ) {
    // 1. Initialization
    let cachedAuctions = null;
    let cachedAuctionMap = new Map();
    const loadCache = () => {
      cachedAuctions = state.auctions;
      if (forSingleAuction) cachedAuctions = [state.viewingAuctionModel];
      cachedAuctionMap = new Map(cachedAuctions.map((a) => [a.id, a]));
    };
    loadCache();
    /**
     * Add API calls to this array if more calls are needed in the future.
     * @type {Array<Promise<Array<{id: number} & import('../../../models/Auction').default>>>}
     */
    const apiCalls = [];
    // 2. Prepare API calls - Filter eligible auctions and extract IDs
    const storeContext = { dispatch, state: rootState }; // provide store to helper utility
    const helperPromises = AuctionAdditionalDataHelpers.helpers.map(
      async ([filterEligible, apiClientCall]) => {
        const eligibleAuctions = await filterEligible(auctions, storeContext);
        if (eligibleAuctions.length === 0) return;
        apiCalls.push(apiClientCall(eligibleAuctions, storeContext));
      },
    );

    // Wait for all filter checks to complete
    await Promise.all(helperPromises);

    // 3. Process results with debounced batch updates
    // When debounce fires, commit currently resolved data; pending APIs continue
    cachedAuctionMap = new Map(cachedAuctions.map((a) => [a.id, a]));
    const updatesMap = new Map();

    /**
     * Process and apply updates from API results
     * @param {Array} results - Array of auction data from API
     */
    const processResults = (results) => {
      results.forEach((auctionData) => {
        // 4. Find the auction in either updatesMap or cachedAuctionMap
        let auction = updatesMap.get(auctionData.id);

        if (!auction) {
          // clone the auction preserving the models or instance classes
          auction = cloneDeep(cachedAuctionMap.get(auctionData.id));
        }

        if (auction) {
          if (auction instanceof Auction) {
            auction.updateAuctionFromAdditionalData(auctionData);
          } else if (auction instanceof SavedAuction) {
            auction.highestRejectedOffer = auctionData.highestRejectedOffer;
            if (auctionData.endedAuctionPurchase) {
              auction.endedAuctionPurchase = auctionData.endedAuctionPurchase;
            }
          } else {
            Object.assign(auction, auctionData);
          }

          updatesMap.set(auction.id, auction);
        }
      });
    };

    /**
     * Commit accumulated updates to store and clear the updates map
     */
    const commitUpdates = () => {
      if (updatesMap.size === 0) return;
      const updates = Array.from(updatesMap.values());
      if (forSingleAuction) commit(UPDATE_VIEWING_AUCTION_MODEL_MUTATION, updates[0]);
      else commit(UPDATE_AUCTION_MUTATION, updates);
      updatesMap.clear();
      loadCache();
    };
    const debounceInterval = 250;
    /**
     * Debounced commit function using lodash.debounce
     * Commits accumulated data 250 milliseconds after the last API call resolves
     */
    const debouncedCommit = debounce(commitUpdates, debounceInterval, {
      leading: false,
      trailing: true,
    });

    // 5. Process each API call independently as it resolves
    apiCalls.forEach((apiPromise) => {
      apiPromise
        .then((results) => {
          if (results) {
            processResults(results);
            debouncedCommit();
          }
        })
        .catch(() => {
          // Silently ignore errors - other API calls continue independently
        });
    });

    // 6. Wait for all API calls to settle (resolved or rejected)
    await Promise.allSettled(apiCalls);
    debouncedCommit.flush();
  },
};

export default actions;
